/**
 * DeviceDefinitionModel - Pure data layer for device definitions (templates)
 * 
 * Note on terminology:
 *   - Code uses "templates" for historical reasons
 *   - User-facing: "device definitions" (blueprints with name, manufacturer, port groups)
 *   - Device instances on canvas reference these definitions via templateId
 * 
 * Wraps state.templates with CRUD operations. No DOM, no side effects beyond data mutation.
 * 
 * Usage:
 *   import { createDeviceDefinitionModel } from './models/DeviceDefinitionModel.js';
 *   const definitionModel = createDeviceDefinitionModel(state);
 *   // Now use definitionModel.getAll(), definitionModel.add(), etc.
 */

import { generateId } from '../utils/index.js';

/**
 * Create a DeviceDefinitionModel that wraps the templates array in the given state object.
 * 
 * @param {Object} state - The state object containing state.templates array
 * @returns {DeviceDefinitionModel}
 */
export function createDeviceDefinitionModel(state) {
    return {
        // ============= READ OPERATIONS =============
        
        /**
         * Get all definitions
         * @returns {DeviceDefinition[]}
         */
        getAll() {
            return state.templates;
        },
        
        /**
         * Get definition by ID
         * @param {string} id
         * @returns {DeviceDefinition|undefined}
         */
        getById(id) {
            return state.templates.find(t => t.id === id);
        },
        
        /**
         * Get definition for a device instance
         * @param {Device} device - Device with templateId property
         * @returns {DeviceDefinition|undefined}
         */
        getForDevice(device) {
            return state.templates.find(t => t.id === device.templateId);
        },
        
        /**
         * Get multiple definitions by IDs (filters out non-existent)
         * @param {string[]} ids
         * @returns {DeviceDefinition[]}
         */
        getByIds(ids) {
            return ids.map(id => state.templates.find(t => t.id === id)).filter(Boolean);
        },
        
        /**
         * Get definition count
         * @returns {number}
         */
        getCount() {
            return state.templates.length;
        },
        
        /**
         * Check if a definition exists
         * @param {string} id
         * @returns {boolean}
         */
        exists(id) {
            return state.templates.some(t => t.id === id);
        },
        
        /**
         * Check if any definition matches predicate
         * @param {function(DeviceDefinition): boolean} predicate
         * @returns {boolean}
         */
        some(predicate) {
            return state.templates.some(predicate);
        },
        
        /**
         * Filter definitions by predicate
         * @param {function(DeviceDefinition): boolean} predicate
         * @returns {DeviceDefinition[]}
         */
        filter(predicate) {
            return state.templates.filter(predicate);
        },
        
        /**
         * Map over all definitions
         * @param {function(DeviceDefinition): T} mapper
         * @returns {T[]}
         */
        map(mapper) {
            return state.templates.map(mapper);
        },
        
        /**
         * Iterate over all definitions
         * @param {function(DeviceDefinition): void} callback
         */
        forEach(callback) {
            state.templates.forEach(callback);
        },
        
        // ============= QUERY OPERATIONS =============
        
        /**
         * Search definitions by name or manufacturer
         * @param {string} query - Search query (case-insensitive)
         * @returns {DeviceDefinition[]}
         */
        search(query) {
            if (!query) return state.templates;
            const lowerQuery = query.toLowerCase();
            return state.templates.filter(t => 
                t.name.toLowerCase().includes(lowerQuery) ||
                (t.manufacturer && t.manufacturer.toLowerCase().includes(lowerQuery))
            );
        },
        
        /**
         * Get definitions by manufacturer
         * @param {string} manufacturer - Exact manufacturer name
         * @returns {DeviceDefinition[]}
         */
        getByManufacturer(manufacturer) {
            return state.templates.filter(t => t.manufacturer === manufacturer);
        },
        
        /**
         * Get all unique manufacturers
         * @returns {string[]}
         */
        getManufacturers() {
            const manufacturers = new Set();
            state.templates.forEach(t => {
                if (t.manufacturer) manufacturers.add(t.manufacturer);
            });
            return Array.from(manufacturers).sort();
        },
        
        /**
         * Compute content hash for deduplication
         * Uses a canonical form: sorted keys, normalized structure
         * @param {DeviceDefinition} definition
         * @returns {string}
         */
        getContentHash(definition) {
            // Build canonical representation (order-independent)
            const canonical = {
                name: definition.name,
                manufacturer: definition.manufacturer || '',
                version: definition.version || '',
                portGroups: (definition.portGroups || []).map(g => ({
                    name: g.name,
                    direction: g.direction,
                    type: g.type,
                    count: g.count,
                    offset: g.offset || 1,
                    labels: g.labels || '',
                    stereoPairs: g.stereoPairs || false,
                    connector: g.connector || ''
                })).sort((a, b) => a.name.localeCompare(b.name))
            };
            
            // Simple string hash (djb2 algorithm)
            const str = JSON.stringify(canonical);
            let hash = 5381;
            for (let i = 0; i < str.length; i++) {
                hash = ((hash << 5) + hash) + str.charCodeAt(i);
                hash = hash & hash; // Convert to 32-bit integer
            }
            // Convert to positive hex string
            return 'hash-' + (hash >>> 0).toString(16);
        },
        
        /**
         * Find definition by content hash (for deduplication)
         * @param {string} contentHash
         * @returns {DeviceDefinition|undefined}
         */
        findByContentHash(contentHash) {
            return state.templates.find(t => this.getContentHash(t) === contentHash);
        },
        
        /**
         * Find existing definition with matching content (regardless of ID)
         * @param {DeviceDefinition} definition
         * @returns {DeviceDefinition|undefined}
         */
        findDuplicate(definition) {
            const hash = this.getContentHash(definition);
            return this.findByContentHash(hash);
        },
        
        // ============= WRITE OPERATIONS =============
        
        /**
         * Add a definition
         * @param {DeviceDefinition} definition
         * @returns {DeviceDefinition} The added definition
         */
        add(definition) {
            state.templates.push(definition);
            return definition;
        },
        
        /**
         * Add multiple definitions
         * @param {DeviceDefinition[]} definitions
         */
        addMany(definitions) {
            definitions.forEach(d => state.templates.push(d));
        },
        
        /**
         * Remove a definition by ID
         * @param {string} id
         * @returns {DeviceDefinition|undefined} The removed definition, if found
         */
        remove(id) {
            const index = state.templates.findIndex(t => t.id === id);
            if (index === -1) return undefined;
            const [removed] = state.templates.splice(index, 1);
            return removed;
        },
        
        /**
         * Remove multiple definitions by IDs
         * @param {string[]} ids
         * @returns {DeviceDefinition[]} The removed definitions
         */
        removeMany(ids) {
            const idSet = new Set(ids);
            const removed = state.templates.filter(t => idSet.has(t.id));
            state.templates = state.templates.filter(t => !idSet.has(t.id));
            return removed;
        },
        
        /**
         * Clear all definitions
         * @returns {DeviceDefinition[]} The removed definitions
         */
        clear() {
            const removed = [...state.templates];
            state.templates = [];
            return removed;
        },
        
        /**
         * Update a definition by ID (merges changes)
         * @param {string} id
         * @param {Partial<DeviceDefinition>} changes
         * @returns {DeviceDefinition|undefined} The updated definition, if found
         */
        update(id, changes) {
            const definition = state.templates.find(t => t.id === id);
            if (!definition) return undefined;
            Object.assign(definition, changes);
            return definition;
        },
        
        /**
         * Replace all definitions (for loading patches)
         * @param {DeviceDefinition[]} definitions
         */
        replaceAll(definitions) {
            // Ensure we always have an array (never undefined/null)
            const safeDefinitions = definitions || [];
            
            // [ISROUTER] Log templates being replaced, with isRouter status
            const routerCount = safeDefinitions.filter(t => t.isRouter).length;
            console.log(`[ISROUTER] DeviceDefinitionModel.replaceAll: ${routerCount}/${safeDefinitions.length} templates with isRouter=true`);
            safeDefinitions.forEach(t => {
                if (t.isRouter) {
                    console.log(`[ISROUTER] DeviceDefinitionModel.replaceAll: template "${t.name}" isRouter=${t.isRouter}`);
                }
            });
            
            state.templates = safeDefinitions;
        },
        
        // ============= FACTORY =============
        
        /**
         * Create and add a new definition
         * @param {string} name
         * @param {PortGroup[]} portGroups
         * @param {string} [manufacturer='']
         * @param {string} [version='']
         * @returns {DeviceDefinition}
         */
        create(name, portGroups = [], manufacturer = '', version = '') {
            // Normalize all group fields
            const normalizedGroups = portGroups.map(g => {
                const normalized = {
                    name: g.name,
                    direction: g.direction,
                    type: g.types && g.types.length > 1 ? g.types : (g.type || 'audio'),
                    count: parseInt(g.count) || 1
                };
                // Include offset if not default (1)
                if (g.offset && g.offset !== 1) {
                    normalized.offset = parseInt(g.offset);
                }
                // Include labels if provided
                if (g.labels && g.labels.trim()) {
                    normalized.labels = g.labels.trim();
                }
                // Include stereoPairs if true
                if (g.stereoPairs) {
                    normalized.stereoPairs = true;
                }
                // Include connector type if specified
                if (g.connector) {
                    normalized.connector = g.connector;
                }
                return normalized;
            });

            const definition = {
                id: generateId(),
                name,
                manufacturer: manufacturer || '',
                version: version || '',
                portGroups: normalizedGroups
            };
            
            state.templates.push(definition);
            return definition;
        },
        
        /**
         * Clone a definition with new ID
         * @param {DeviceDefinition} definition
         * @param {string} [nameSuffix=' (Copy)'] - Suffix to add to name
         * @returns {DeviceDefinition}
         */
        clone(definition, nameSuffix = ' (Copy)') {
            const cloned = {
                id: generateId(),
                name: definition.name + nameSuffix,
                manufacturer: definition.manufacturer || '',
                version: definition.version || '',
                portGroups: (definition.portGroups || []).map(g => ({
                    ...JSON.parse(JSON.stringify(g)),
                    id: generateId()
                }))
            };
            // DEFENSIVE: Preserve isRouter - use explicit check for key presence
            if ('isRouter' in definition) {
                cloned.isRouter = !!definition.isRouter;
                if (cloned.isRouter) {
                    console.log(`[ISROUTER] DeviceDefinitionModel.clone: template "${cloned.name}" isRouter=${cloned.isRouter} (preserved from "${definition.name}")`);
                }
            }
            state.templates.push(cloned);
            return cloned;
        },
        
        /**
         * Deep copy a definition (for external use, doesn't add to state)
         * @param {DeviceDefinition} definition
         * @returns {DeviceDefinition}
         */
        deepCopy(definition) {
            return JSON.parse(JSON.stringify(definition));
        }
    };
}
